import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../config/supabaseClient";
import { FaBookOpen, FaChevronRight, FaArrowLeft } from "react-icons/fa";

// Normalize strings for matching
const normalize = (str) => str.toLowerCase().replace(/\s+/g, " ").trim();

const SectionsTopicsPage = () => {
  const { section } = useParams(); // This is the section clicked
  const navigate = useNavigate();

  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTopics(section);
  }, [section]);

  // 🔹 Fetch topics for the section
  const fetchTopics = async (subCategory) => {
    setLoading(true);
    try {
      let query = supabase.from("topics").select("*");
      if (subCategory && (subCategory.toLowerCase() === "applied grammar" || subCategory.toLowerCase() === "grammar")) {
        query = query.or(`class.eq."Applied Grammar",subCategory.eq."Applied Grammar",class.eq."Grammar",subCategory.eq."Grammar"`);
      } else {
        query = query.or(`class.eq."${subCategory}",subCategory.eq."${subCategory}"`);
      }
      const { data, error } = await query;

      if (error) throw error;

      const topicData = {};
      data.forEach((doc) => {
        if (doc.topic) topicData[normalize(doc.topic)] = doc;
      });

      // Sort topics
      const sortedKeys = Object.keys(topicData).sort((a, b) => {
        const numA = parseInt(a.match(/^\d+/)?.[0]);
        const numB = parseInt(b.match(/^\d+/)?.[0]);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      });

      const sortedTopics = {};
      sortedKeys.forEach((key) => {
        sortedTopics[key] = topicData[key];
      });

      setTopics(sortedTopics);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
    setLoading(false);
  };

  // 🔹 Handle topic click
  const handleTopicClick = (topicName) => {
    const normalizedKey = normalize(topicName);
    const fileData = topics[normalizedKey];

    if (!fileData) {
      console.warn("Topic not found!", topicName);
      return;
    }

    const topicSlug = topicName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    navigate(`/description/${encodeURIComponent(section)}/${encodeURIComponent(topicSlug)}`);
  };

  // 🔹 Custom Styles
  const containerStyle = {
    maxWidth: "1100px",
    margin: "60px auto 120px",
    padding: "0 24px",
    fontFamily: "'Outfit', 'Inter', -apple-system, sans-serif"
  };

  const headerCardStyle = {
    textAlign: "center",
    marginBottom: "30px"
  };

  const listGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
    marginTop: "20px"
  };

  const topicCardStyle = {
    display: "flex",
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    padding: "16px 20px",
    borderRadius: "16px",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.006)"
  };

  const iconBoxStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "rgba(59, 130, 246, 0.08)",
    color: "#3b82f6",
    marginRight: "16px",
    fontSize: "1.1rem",
    flexShrink: 0
  };

  const textStyle = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#1e293b",
    flexGrow: 1,
    textAlign: "left",
    lineHeight: "1.4"
  };

  const arrowStyle = {
    color: "#94a3b8",
    fontSize: "0.8rem",
    marginLeft: "12px",
    transition: "transform 0.3s"
  };

  return (
    <div style={containerStyle}>
      {/* Back Button */}
      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "20px" }}>
        <button 
          onClick={() => navigate(-1)} 
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "transparent",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            padding: "8px 16px",
            borderRadius: "10px",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#3b82f6";
            e.currentTarget.style.color = "#3b82f6";
            e.currentTarget.style.background = "rgba(59, 130, 246, 0.04)";
            e.currentTarget.style.transform = "translateX(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#e2e8f0";
            e.currentTarget.style.color = "#64748b";
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.transform = "translateX(0)";
          }}
        >
          <FaArrowLeft size={14} /> Back
        </button>
      </div>

      {/* Centered Title & Description Header */}
      <div style={headerCardStyle}>
        <h2 style={{ fontSize: "2.2rem", fontWeight: "800", color: "#1e293b", marginBottom: "12px", letterSpacing: "-0.5px" }}>
          {section}
        </h2>
        <div style={{ height: "4px", width: "80px", background: "linear-gradient(135deg, #4f46e5, #3b82f6)", borderRadius: "10px", margin: "0 auto 20px" }}></div>
        <p style={{ fontSize: "1.1rem", color: "#64748b", fontWeight: "500", maxWidth: "600px", margin: "0 auto" }}>
          Select a topic below to explore resources, grammar rules, examples, and study sheets.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <p style={{ color: "#64748b", fontSize: "1.1rem", fontWeight: "600" }}>Loading topics...</p>
        </div>
      ) : Object.keys(topics).length > 0 ? (
        <div style={listGridStyle}>
          {Object.keys(topics).map((key, i) => (
            <div
              key={i}
              style={topicCardStyle}
              onClick={() => handleTopicClick(key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow = "0 12px 20px -8px rgba(59, 130, 246, 0.15)";
                const arrow = e.currentTarget.querySelector(".topic-arrow");
                if (arrow) arrow.style.transform = "translateX(3px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0,0,0,0.02)";
                const arrow = e.currentTarget.querySelector(".topic-arrow");
                if (arrow) arrow.style.transform = "translateX(0)";
              }}
            >
              <div style={iconBoxStyle}>
                <FaBookOpen />
              </div>
              <span style={textStyle}>
                {topics[key]?.topic || key}
              </span>
              <span className="topic-arrow" style={arrowStyle}>
                <FaChevronRight />
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "80px 20px", background: "#f8fafc", borderRadius: "20px" }}>
          <p style={{ color: "#64748b", fontSize: "1.1rem", fontWeight: "600", margin: 0 }}>
            No topics available for this section.
          </p>
        </div>
      )}
    </div>
  );
};

export default SectionsTopicsPage;
